import type { MergePreview } from '@customerio/hydra-ui-api';
import type { IdentifiersConfig } from '@/features/people';
import type { Customer } from '@/services/db/customer/entity';

/** The three profile cards the wizard renders: the pair being merged and the projected result. */
export const ProfileType = {
  Primary: 'primary',
  Secondary: 'secondary',
  Merged: 'merged',
} as const;

export type ProfileType = (typeof ProfileType)[keyof typeof ProfileType];

/** The analytics label for each card. */
export const PROFILE_TYPE_LABELS = {
  primary: 'Primary',
  secondary: 'Secondary',
  merged: 'Merged',
} satisfies Record<ProfileType, string>;

/** The two cards a person can be selected into. */
export type SelectableProfileType = typeof ProfileType.Primary | typeof ProfileType.Secondary;

export const Step = {
  Setup: 'setup',
  Preview: 'preview',
} as const;

export type Step = (typeof Step)[keyof typeof Step];

/** The three statements a person must agree to before the merge can be committed. */
export type MergeConfirmations = {
  /** Merging may add the person to segments and trigger messages. */
  segments: boolean;
  /** The primary inherits the secondary's automation and delivery history. */
  history: boolean;
  /** The original people cannot be restored. */
  irreversible: boolean;
};

export const UNCONFIRMED: MergeConfirmations = { segments: false, history: false, irreversible: false };

/**
 * Where the wizard stands. The confirmations exist only on the review step, so
 * leaving it forgets them and re-entering starts unchecked.
 */
export type WizardStep = { id: typeof Step.Setup } | { id: typeof Step.Preview; confirmations: MergeConfirmations };

export type SelectedCustomers = Record<SelectableProfileType, Customer | null>;

export type MergedAttributes = MergePreview['attributes'];
export type MergeSummary = MergePreview['summary'];

/** The profile fields the attribute list derives from. */
export type ProfileSource = Pick<Customer, 'attributes' | 'identifiers'>;

/** The workspace facts and product words every card reads; resolved once by the page. */
export type MergeWorkspaceContext = {
  identifiers: IdentifiersConfig | null | undefined;
  isMultiIdentifier: boolean;
  isEmailIdentified: boolean;
  canIdentifyByPhone: boolean;
  /** Avatars may look the person's picture up by email. */
  withGravatar: boolean;
  /** Where the workspace's identifiers are managed. */
  identifiersSettingsHref: string;
  terms: {
    peopleSingular: string;
    peoplePlural: string;
    peopleTitlePlural: string;
    customersSingular: string;
    campaignsSingular: string;
  };
};
